export const DOC_LABELS: Record<string, string> = {
   SOCIAL_CONTRACT: 'Contrato social',
};

export const ALL_IDEZ_DOC_TYPES = ['SOCIAL_CONTRACT'] as const;

export interface IdezDocumentSection {
   key: string;
   title: string;
   required?: boolean;
   types: string[];
}

export interface IdezBiometricEntity {
   id: number;
   entity_id: string;
   entity_type?: string | null;
   document?: string | null;
   name?: string | null;
   session_status?: string | null;
   verification_status?: string | null;
   token_expires_at?: string | null;
   token_expired?: boolean;
   can_validate?: boolean;
   needs_resend?: boolean;
   biometrics_url?: string | null;
}

export function requiresSocialContract(isBusiness: boolean, establishmentType?: string | null): boolean {
   return isBusiness && String(establishmentType ?? '').toUpperCase() !== 'MEI';
}

export function collectDocumentTypes(
   existingDocuments: Record<string, any>,
   pendingFiles: Record<string, File | null>,
): string[] {
   const types = new Set<string>();

   Object.entries(existingDocuments).forEach(([type, doc]) => {
      if (!doc) {
         return;
      }

      const status = doc.upload_status ?? 'uploaded';
      if (['uploaded', 'staged', 'pending_idez'].includes(status) || doc.idez_document_id) {
         types.add(type.toUpperCase());
      }
   });

   Object.entries(pendingFiles).forEach(([type, file]) => {
      if (file) {
         types.add(type.toUpperCase());
      }
   });

   return Array.from(types);
}

export function buildDocumentSections(
   kycRequirements: any,
   isBusiness: boolean,
   establishmentType?: string | null,
): IdezDocumentSection[] {
   if (kycRequirements?.document_sections?.length) {
      return kycRequirements.document_sections.filter((section: IdezDocumentSection) => {
         if (section.key === 'social_contract') {
            return requiresSocialContract(isBusiness, establishmentType);
         }

         return true;
      });
   }

   if (!requiresSocialContract(isBusiness, establishmentType)) {
      return [];
   }

   return [
      {
         key: 'social_contract',
         title: 'Contrato social',
         required: false,
         types: ['SOCIAL_CONTRACT'],
      },
   ];
}

export function buildDocumentFields(
   sections: IdezDocumentSection[],
   existingDocuments: Record<string, any>,
   pendingFiles: Record<string, File | null>,
   documentErrors: Record<string, string>,
) {
   return sections.flatMap((section) =>
      section.types.map((type) => ({
         sectionKey: section.key,
         sectionTitle: section.title,
         sectionRequired: Boolean(section.required),
         type,
         label: DOC_LABELS[type] ?? type,
         existing: existingDocuments[type],
         pending: pendingFiles[type],
         error: documentErrors[type],
      })),
   );
}

export function biometricsStatusLabel(entity: IdezBiometricEntity): string {
   if (entity.verification_status === 'approved') {
      return 'Identidade aprovada';
   }

   if (entity.verification_status === 'rejected') {
      return 'Identidade rejeitada';
   }

   if (entity.verification_status === 'expired' || entity.token_expired) {
      return 'Sessão expirada';
   }

   if (entity.session_status === 'completed') {
      return 'Biometria concluída — aguardando análise';
   }

   return 'Validação pendente';
}

export function socialContractHint(isBusiness: boolean, establishmentType?: string | null): string {
   if (!requiresSocialContract(isBusiness, establishmentType)) {
      return 'A validação de identidade é feita pela WebView de biometria da Idez.';
   }

   return 'Após criar a conta, conclua a biometria e envie o contrato social (opcional, mas recomendado para PJ).';
}

export function openBiometricsUrl(url: string): void {
   window.open(url, '_blank', 'noopener,noreferrer');
}
